import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NodeInstall, NodePack } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';

const STATUS: Readonly<Record<NodeInstall['status'], string>> = {
  awaiting_approval: 'Waiting for your approval', queued: 'Queued', installing: 'Installing…', restarting: 'Restarting ComfyUI…',
  done: 'Installed', failed: 'Failed', declined: 'Declined',
};
const ACTIVE = new Set<NodeInstall['status']>(['queued', 'installing', 'restarting']);

function useNodePackKeys(machineId: string) {
  const queryClient = useQueryClient();
  return () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['node-packs', machineId] }),
    queryClient.invalidateQueries({ queryKey: ['node-installs', machineId] }),
  ]);
}

function InstallRow({ machineId, install }: { machineId: string; install: NodeInstall }) {
  const refresh = useNodePackKeys(machineId);
  const decide = useMutation({ mutationFn: (approve: boolean) => api.decideNodeInstall(machineId, install.id, approve), onSuccess: refresh });
  return (
    <li className="run">
      <div className="run-head">
        <strong>{install.packId}</strong>
        <span className="badge">{STATUS[install.status]}</span>
        {install.requestedBy === 'ai' && <span className="muted">asked by AI</span>}
        {install.status === 'awaiting_approval' && (
          <>
            <button type="button" disabled={decide.isPending} onClick={() => { decide.mutate(true); }}>Approve</button>
            <button type="button" className="button-quiet" disabled={decide.isPending} onClick={() => { decide.mutate(false); }}>Decline</button>
          </>
        )}
      </div>
      {install.reason && <p className="muted">“{install.reason}”</p>}
      {install.error && <p className={install.status === 'done' ? 'muted' : 'form-error'}>{install.error}</p>}
      {decide.isError && <p role="alert" className="form-error">{errorMessage(decide.error)}</p>}
    </li>
  );
}

function PackSearch({ machineId }: { machineId: string }) {
  const refresh = useNodePackKeys(machineId);
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const results = useQuery({ queryKey: ['node-pack-search', machineId, submitted], queryFn: () => api.searchNodePacks(machineId, submitted), enabled: submitted !== '' });
  const install = useMutation({ mutationFn: (pack: NodePack) => api.requestNodeInstall(machineId, pack.id), onSuccess: refresh });
  return (
    <>
      <form className="machine-actions" onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim()); }}>
        <label className="visually-hidden" htmlFor="pack-search">Search node packs</label>
        <input id="pack-search" placeholder="e.g. openpose, controlnet, upscale" value={query} onChange={(e) => { setQuery(e.target.value); }} />
        <button type="submit" disabled={!query.trim()}>Search</button>
      </form>
      {results.isError && <p role="alert" className="form-error">{errorMessage(results.error)}</p>}
      {results.data?.packs.length === 0 && <p className="muted">Nothing found.</p>}
      <ul className="run-list">
        {results.data?.packs.map((pack) => (
          <li key={pack.repository || pack.id} className="run">
            <div className="run-head">
              <strong>{pack.title || pack.id}</strong>
              <span className="muted">{pack.author}{pack.stars ? ` · ★ ${pack.stars}` : ''}</span>
              {pack.installed && <span className="badge badge-pending">Installed</span>}
              {!pack.installed && pack.registry && <button type="button" aria-label={`Install ${pack.title || pack.id}`} disabled={install.isPending} onClick={() => { install.mutate(pack); }}>Install</button>}
              {!pack.registry && <span className="muted">not in the Comfy Registry — install it with the Manager in ComfyUI</span>}
            </div>
            <p className="muted">{pack.description}</p>
          </li>
        ))}
      </ul>
      {install.isError && <p role="alert" className="form-error">{errorMessage(install.error)}</p>}
    </>
  );
}

/** Custom node packs via ComfyUI's own manager; the AI's installs wait for Approve (doc 34). */
export function NodePacksPanel({ machineId }: { machineId: string }) {
  const refresh = useNodePackKeys(machineId);
  const packs = useQuery({
    queryKey: ['node-packs', machineId], queryFn: () => api.nodePacks(machineId), retry: false,
    refetchInterval: (q) => (q.state.data?.manager.enabling ? 3000 : false),
  });
  const installs = useQuery({
    queryKey: ['node-installs', machineId], queryFn: () => api.nodeInstalls(machineId),
    refetchInterval: (q) => (q.state.data?.installs.some((i) => ACTIVE.has(i.status)) ? 3000 : false),
  });
  const enable = useMutation({ mutationFn: () => api.enableNodePacks(machineId), onSuccess: refresh });
  const manager = packs.data?.manager;
  return (
    <section className="panel" aria-labelledby="nodepacks-title">
      <h2 id="nodepacks-title">Custom nodes</h2>
      <p className="muted">Extra node packs from the Comfy Registry, installed by ComfyUI's own manager. They run code on this machine — install what you trust.</p>
      {packs.isError && <p role="alert" className="form-error">{errorMessage(packs.error)}</p>}
      {manager && !manager.enabled && (
        <div className="notice">
          <p>{manager.enabling ? 'Enabling custom nodes — installing the manager and restarting ComfyUI…' : 'Custom nodes are not enabled on this machine yet.'}</p>
          {manager.error && <p className="form-error">{manager.error}</p>}
          {!manager.enabling && <button type="button" disabled={enable.isPending} onClick={() => { enable.mutate(); }}>Enable custom nodes</button>}
        </div>
      )}
      {(installs.data?.installs.length ?? 0) > 0 && <ul className="run-list">{installs.data?.installs.slice(0, 8).map((i) => <InstallRow key={i.id} machineId={machineId} install={i} />)}</ul>}
      {manager?.enabled && <PackSearch machineId={machineId} />}
      {manager?.enabled && packs.data && packs.data.installed.length > 0 && (
        <details>
          <summary>{packs.data.installed.length} installed</summary>
          <ul>{packs.data.installed.map((p) => <li key={p.dir}>{p.id} <span className="muted">{p.version}{p.enabled ? '' : ' (disabled)'}</span></li>)}</ul>
        </details>
      )}
    </section>
  );
}
